import React, { useState } from 'react';
import { isModifiedClick } from '../../utils/navigation';
import { PageHeader } from '../../components/common/PageHeader';
import {
  Award,
  Search,
  Lock,
  CheckCircle2,
  Clock,
  Users,
  Swords,
  Layers,
  GraduationCap
} from 'lucide-react';

interface SubRankDetail {
  code: string;
  name: string;
  badgeImg: string;
  culaLabel: string;
  roleDescription: string;
}

interface SyllabusLevel {
  id: string;
  module: string;
  title: string;
  categoryTag: string;
  targetAudience: string;
  duration: string;
  subRanks: SubRankDetail[];
  description: string;
  highlights: string[];
  examRequirements: string[];
}

export const PublicSyllabusPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('Semua');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCula, setSelectedCula] = useState<Record<string, number>>({
    hitam: 0,
    putih: 0,
    merah: 0,
    kuning: 0,
    biru: 0,
    'guru-utama': 0,
  });

  const levels: SyllabusLevel[] = [
    {
      id: 'hitam',
      module: 'Bengkung Hitam',
      title: 'Tunas Pendekar (Asas Disiplin & Gerak Elak)',
      categoryTag: 'Tunas Pendekar',
      targetAudience: 'Kanak-kanak & Pesilat Baru (Umur 7 - 12 Tahun)',
      duration: '6 - 9 Bulan',
      description:
        'Peringkat asas pembinaan peribadi pesilat. Silibus memberi tumpuan kepada Kekuda Asas 1 hingga 8, senaman pemanasan separuh tiang, tari elakan, serta teknik gulingan selamat. Teras silibus ini ialah "Buah Poket" — kaedah saintifik meloloskan diri daripada cengkaman lawan tanpa perlu mencederakan.',
      subRanks: [
        {
          code: 'Hitam',
          name: 'Tunas Pendekar Asas',
          badgeImg: '/badges/hitam0-tunas-pendekar.png',
          culaLabel: '',
          roleDescription: 'Pengenalan disiplin, hormat guru, dan asas keselamatan gelanggang'
        },
        {
          code: 'Hitam 1',
          name: 'Tunas Pendekar Muda',
          badgeImg: '/badges/hitam1-tunas-pendekar-muda.png',
          culaLabel: 'Cula 1',
          roleDescription: 'Kekuda seimbang, senaman separuh tiang dan tapak 3'
        },
        {
          code: 'Hitam 2',
          name: 'Tunas Pendekar Sulung',
          badgeImg: '/badges/hitam2-tunas-pendekar-sulung.png',
          culaLabel: 'Cula 2',
          roleDescription: 'Langkah elakan berpasangan dan tangkisan asas'
        },
        {
          code: 'Hitam 3',
          name: 'Tunas Pendekar Kanan',
          badgeImg: '/badges/hitam3-tunas-pendekar-kanan.png',
          culaLabel: 'Cula 3',
          roleDescription: 'Penguasaan Buah Poket untuk pelepasan diri tanpa kecederaan'
        }
      ],
      highlights: [
        'Kekuda Asas 1 - 8 (Kuda-kuda rendah & stabil)',
        'Senaman Separuh Tiang & Pemanasan Tubuh',
        'Seni Tari Elakan & Langkah Berpasangan',
        'Teknik Buah Poket (Pelepasan pegangan selamat)',
        'Gulingan & Jatuhan Selamat'
      ],
      examRequirements: [
        'Kehadiran latihan gelanggang minimum 80%',
        'Hafalan Ikrar Pesilat & Adab Gelanggang',
        'Ujian Praktikal Kekuda & Aplikasi Buah Poket'
      ]
    },
    {
      id: 'putih',
      module: 'Bengkung Putih',
      title: 'Pesilat (Seni Tempur & Asas Senjata)',
      categoryTag: 'Pesilat',
      targetAudience: 'Remaja & Dewasa (Umur 13 Tahun Ke Atas / Lepasan Tunas)',
      duration: '9 - 12 Bulan',
      description:
        'Mendalami seni tempur tangan kosong yang sistematik menerusi Jurus Asas 1 hingga 20 serta Buah Pukulan 1 & 2. Pesilat dilatih membaca tempo pergerakan musuh, menyerap daya hentaman, dan mula diperkenalkan dengan asas senjata tradisional seperti Cindai dan tikaman pisau pendek.',
      subRanks: [
        {
          code: 'Putih',
          name: 'Pesilat Asas',
          badgeImg: '/badges/putih0.png',
          culaLabel: '',
          roleDescription: 'Pemantapan jurus tumbukan lurus, tendangan sisi, dan elak pantas'
        },
        {
          code: 'Putih 1',
          name: 'Pesilat Muda',
          badgeImg: '/badges/putih1-pesilat-muda.png',
          culaLabel: 'Cula 1',
          roleDescription: 'Penguasaan Jurus Asas 1-10 dan Buah Pukulan 1'
        },
        {
          code: 'Putih 2',
          name: 'Pesilat Kanan',
          badgeImg: '/badges/putih2-pesilat-kanan.png',
          culaLabel: 'Cula 2',
          roleDescription: 'Penguasaan Jurus Asas 11-20 dan Buah Pukulan 2'
        },
        {
          code: 'Putih 3',
          name: 'Pesilat Sulung',
          badgeImg: '/badges/putih3-pesilat-sulung.png',
          culaLabel: 'Cula 3',
          roleDescription: 'Asas tangkisan Cindai, Pisau, dan Ujian Ketahanan 5 Lawan 1'
        }
      ],
      highlights: [
        'Jurus Asas 1 - 20 (Sains tumbukan & tangkisan)',
        'Buah Pukulan 1 & 2 (Serangan balas terkawal)',
        'Asas Senjata Cindai & Pisau Pendek',
        'Latihan Ketahanan & Kekuatan Fizikal Tempur',
        'Ujian Aplikasi Tempur 5 Lawan 1 (Tangan Kosong)'
      ],
      examRequirements: [
        'Lulus Ujian Bertulis Sukatan Silibus Asas',
        'Demonstrasi Lengkap Jurus Asas 1 - 20 tanpa cacat',
        'Lulus Ujian Ketahanan Aplikasi 5 Lawan 1'
      ]
    },
    {
      id: 'merah',
      module: 'Bengkung Merah',
      title: 'Pembantu Jurulatih (Patahan Sendi & Senjata Tradisional)',
      categoryTag: 'Pembantu Jurulatih',
      targetAudience: 'Pesilat Kanan & Bakal Pembantu Pengajar Gelanggang',
      duration: '12 - 18 Bulan',
      description:
        'Melangkah ke fasa lanjutan yang menuntut kematangan emosi tinggi. Silibus merangkumi teknik patahan sendi tulang, kuncian perenggan saraf, serta pengendalian senjata tajam tradisional berisiko tinggi termasuk Sembat, Parang Panjang, dan Belati. Pesilat turut memikul peranan sebagai pembantu jurulatih gelanggang.',
      subRanks: [
        {
          code: 'Merah',
          name: 'Pembantu Jurulatih Asas',
          badgeImg: '/badges/merah0-pembantu-jurulatih.png',
          culaLabel: '',
          roleDescription: 'Membimbing pemanasan tubuh dan pengawasan pesilat bengkung putih'
        },
        {
          code: 'Merah 1',
          name: 'Pembantu Jurulatih Muda',
          badgeImg: '/badges/merah1-pembantu-jurulatih-muda.png',
          culaLabel: 'Cula 1',
          roleDescription: 'Penguasaan Buah Pukulan 3 dan kuncian sendi pergelangan tangan'
        },
        {
          code: 'Merah 2',
          name: 'Pembantu Jurulatih Kanan',
          badgeImg: '/badges/merah2-pembantu-jurulatih-kanan.png',
          culaLabel: 'Cula 2',
          roleDescription: 'Penguasaan Buah Pukulan 4 & 5 serta senjata Sembat'
        },
        {
          code: 'Merah 3',
          name: 'Pembantu Jurulatih Sulung',
          badgeImg: '/badges/merah3-pembantu-jurulatih-sulung.png',
          culaLabel: 'Cula 3',
          roleDescription: 'Pengendalian Parang Panjang, Belati, dan Ujian Senjata Bahaya'
        }
      ],
      highlights: [
        'Buah Pukulan 3, 4 & 5 (Kemusnahan mekanikal lawan)',
        'Teknik Kuncian Sendi & Patahan Tulang',
        'Pengendalian Senjata: Sembat & Tongkat Pendek',
        'Seni Senjata Bahaya: Parang Panjang & Belati',
        'Ujian Senjata Berisiko Tinggi 5 Lawan 1'
      ],
      examRequirements: [
        'Pengalaman pembantu latihan minimum 6 bulan di gelanggang',
        'Penguasaan selamat senjata tajam tanpa risiko kecederaan',
        'Lulus Ujian Bertulis Pedagogi Pengajaran Silat'
      ]
    },
    {
      id: 'kuning',
      module: 'Bengkung Kuning',
      title: 'Jurulatih & Guru (Seni Tempur Ketinggian & Rahsia Pendekar)',
      categoryTag: 'Jurulatih',
      targetAudience: 'Ketua Gelanggang, Jurulatih Bertauliah & Guru',
      duration: '2 - 3 Tahun Berterusan',
      description:
        'Tahap kepimpinan dan keilmuan mendalam. Merangkumi kebijaksanaan geometri ruang (Tapak 7 & Tapak 4), Seni 7 Haiwan Rimba, serta kepakaran senjata maut Kerambit Awang Sulung. Diiringi ilmu asas Perubatan Tradisional Melayu (urutan sendi, patah tulang, dan ramuan herba) serta rahsia adab kependekaran.',
      subRanks: [
        {
          code: 'Kuning',
          name: 'Jurulatih Asas',
          badgeImg: '/badges/kuning0-penolong-jurulatih.png',
          culaLabel: '',
          roleDescription: 'Memimpin sesi gelanggang dan melatih pesilat baharu'
        },
        {
          code: 'Kuning 1-3',
          name: 'Jurulatih Muda',
          badgeImg: '/badges/kuning1-junior-instructor-jurulatih-muda.png',
          culaLabel: 'Cula 1–3',
          roleDescription: 'Penguasaan Tapak 7 & Tapak 4 serta Seni 7 Haiwan Rimba'
        },
        {
          code: 'Kuning 4-6',
          name: 'Jurulatih Kanan',
          badgeImg: '/badges/kuning5-5th-degree-instructor-jurulatih-kanan.png',
          culaLabel: 'Cula 4–6',
          roleDescription: 'Penguasaan Kerambit Awang Sulung dan Asas Perubatan Herba'
        },
        {
          code: 'Kuning 7',
          name: 'Jurulatih Sulung',
          badgeImg: '/badges/kuning7-7th-degree-instructor-jurulatih-sulung.png',
          culaLabel: 'Cula 7',
          roleDescription: 'Pentauliahan penuh Majlis Guru & pemeriksa ujian bengkung'
        }
      ],
      highlights: [
        'Geometri Tempur: Kebijaksanaan Tapak 7 & Tapak 4',
        'Seni 7 Haiwan Rimba (Harimau, Helang, Buaya, dll)',
        'Seni Maut Kerambit Awang Sulung',
        'Perubatan Tradisional Melayu & Rawatan Sendi',
        'Kaedah Pengurusan Gelanggang & Pentadbiran ROS'
      ],
      examRequirements: [
        'Pentauliahan rasmi Majlis Guru ASMM',
        'Sijil Jurulatih Bertauliah Skim Sukan Kebangsaan (jika ada)',
        'Kelulusan Panel Penguji Khas Mahaguru'
      ]
    },
    {
      id: 'biru',
      module: 'Bengkung Biru Samudera',
      title: 'Dewan Pendekar',
      categoryTag: 'Dewan Pendekar',
      targetAudience: 'Dewan Pendekar Perguruan ASMM',
      duration: 'Anugerah Kependekaran Sepanjang Hayat',
      description:
        'Mewakili darjat kependekaran tinggi perguruan berakar daripada falsafah "Lautan Membiru" — lambang ilmu persilatan, sains fizik gerak, dan kebatinan yang luas tanpa sempadan. Tokoh berbengkung biru bertindak sebagai rujukan strategik, penjaga adab persilatan, serta pemegang amanah keharmonian silat Melayu.',
      subRanks: [
        {
          code: 'Biru',
          name: 'Dewan Pendekar',
          badgeImg: '/badges/biru.png',
          culaLabel: '',
          roleDescription: 'Rujukan taktik tempur dan pemelihara adab kependekaran'
        }
      ],
      highlights: [
        'Falsafah Tertinggi Kependekaran Lautan Membiru',
        'Taktik Pertahanan Komuniti & Kawalan Krisis',
        'Pemeliharaan Warisan Salasilah Asli',
        'Sidang Lembaga Majlis Pendekar Perguruan'
      ],
      examRequirements: [
        'Anugerah Khas Lembaga Majlis Perguruan ASMM',
        'Jasa bakti berterusan melebihi 5 tahun dalam persatuan',
        'Ihsan dan integriti tinggi tanpa sebarang rekod disiplin'
      ]
    },
    {
      id: 'guru-utama',
      module: 'Bengkung Guru Utama',
      title: 'Pucuk Pimpinan Tertinggi & Pewaris Sanad',
      categoryTag: 'Guru Utama',
      targetAudience: 'Pucuk Pimpinan Tertinggi & Pewaris Sanad Diraja',
      duration: 'Pewaris Sanad Sepanjang Hayat',
      description:
        'Puncak kepimpinan dan punca sanad rasmi Persatuan Seni Silat Awang Sulung Merah Muda Kedah Darul Aman. Bertanggungjawab mutlak memelihara amanah salasilah Pawang Diraja, mengetuai Majlis Penetapan Syarat Perguruan, serta mengijazahkan kurikulum rasmi kepada seluruh barisan jurulatih dan pesilat di seluruh Malaysia.',
      subRanks: [
        {
          code: 'Guru Utama',
          name: 'Guru Utama Perguruan ASMM',
          badgeImg: '/badges/guruUtama.png',
          culaLabel: '',
          roleDescription: 'Pucuk pimpinan tertinggi dan punca sanad keilmuan ASMM'
        }
      ],
      highlights: [
        'Punca Sanad Warisan Silat Pawang Diraja Patani',
        'Majlis Pengijazahan Silibus & Pembukaan Gelanggang',
        'Penetapan Syarat Kenaikan Bengkung Rasmi',
        'Penaung Majlis Perguruan & Hubungan Kerajaan (ROS)'
      ],
      examRequirements: [
        'Pewaris sah salasilah institusi perguruan',
        'Penerima ijazah sanad langsung daripada barisan Mahaguru terdahulu'
      ]
    }
  ];

  const handleNavigate = (e: React.MouseEvent, path: string) => {
    if (isModifiedClick(e)) return;
    e.preventDefault();
    window.history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const filteredLevels = levels.filter((lvl) => {
    const matchesSearch =
      lvl.module.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lvl.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lvl.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lvl.highlights.some((h) => h.toLowerCase().includes(searchQuery.toLowerCase()));

    if (!matchesSearch) return false;

    if (selectedCategory === 'Semua') return true;
    if (selectedCategory === 'Hitam') return lvl.id === 'hitam';
    if (selectedCategory === 'Putih') return lvl.id === 'putih';
    if (selectedCategory === 'Merah') return lvl.id === 'merah';
    if (selectedCategory === 'Kuning') return lvl.id === 'kuning';
    if (selectedCategory === 'Biru') return lvl.id === 'biru';
    if (selectedCategory === 'Guru Utama') return lvl.id === 'guru-utama';

    return true;
  });

  return (
    <div className="w-full space-y-6 min-h-[75vh] pb-16">
      {/* 1. Page Header (Standardized Across Portal - referenced from /dokumen) */}
      <PageHeader
        badgeText="Kurikulum Bertauliah"
        badgeIcon={Award}
        title="Silibus & Kurikulum Bengkung"
        titleBadge="6 Tahap"
      >
        <a
          href="/login"
          onClick={(e) => handleNavigate(e, '/login')}
          className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer shrink-0"
        >
          <Lock className="w-4 h-4" />
          <span>Portal Ahli</span>
        </a>
      </PageHeader>



      {/* 3. Search & Category Filter Section (referenced from /dokumen) */}
      <div className="bg-white p-3.5 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          {/* Category Filter Buttons with smooth touch scroll on mobile */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
            {[
              { id: 'Semua', label: 'Semua' },
              { id: 'Hitam', label: 'Bengkung Hitam' },
              { id: 'Putih', label: 'Bengkung Putih' },
              { id: 'Merah', label: 'Bengkung Merah' },
              { id: 'Kuning', label: 'Bengkung Kuning' },
              { id: 'Biru', label: 'Bengkung Biru' },
              { id: 'Guru Utama', label: 'Guru Utama' },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-extrabold whitespace-nowrap transition-all cursor-pointer shrink-0 ${selectedCategory === cat.id
                  ? 'bg-asmm-red text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-64 shrink-0">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari silibus, jurus, bengkung..."
              className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-medium text-slate-900"
            />
          </div>
        </div>
      </div>

      {/* 4. Bengkung Level Cards Flow (PROPER VERTICAL FLOW: Bengkung Image Top -> Newline Details Below) */}
      <div className="space-y-4 sm:space-y-6">
        {filteredLevels.length === 0 ? (
          <div className="p-8 sm:p-12 text-center text-slate-500 text-xs font-bold bg-white rounded-2xl sm:rounded-3xl border border-slate-200">
            Tiada sukatan silibus dijumpai untuk padanan carian ini.
          </div>
        ) : (
          filteredLevels.map((level) => {
            const currentCulaIdx = selectedCula[level.id] || 0;
            const activeSubRank = level.subRanks[currentCulaIdx] || level.subRanks[0];

            return (
              <div
                key={level.id}
                id={`tahap-${level.id}`}
                className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-7 space-y-4 sm:space-y-6 transition-all hover:border-slate-300"
              >
                {/* 4.1 Card Header Info */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 border-b border-slate-100 pb-3 sm:pb-4">
                  <div>
                    <h3 className="text-base sm:text-lg font-extrabold text-slate-900 leading-snug">
                      {level.module} — {level.title}
                    </h3>
                    <div className="text-[11px] text-slate-400 font-semibold mt-0.5">
                      Kategori: {level.categoryTag}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <span className="text-[11px] sm:text-xs text-slate-600 font-medium bg-slate-50 px-2.5 sm:px-3 py-1 rounded-xl border border-slate-200 inline-flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{level.targetAudience}</span>
                    </span>
                    <span className="text-[11px] sm:text-xs text-slate-600 font-medium bg-slate-50 px-2.5 sm:px-3 py-1 rounded-xl border border-slate-200 inline-flex items-center gap-1.5 shrink-0">
                      <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{level.duration}</span>
                    </span>
                  </div>
                </div>

                {/* 4.2 Bengkung Showcase Canvas (Bengkung Image Prominently Displayed at Top) */}
                <div className="p-3.5 sm:p-6 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-3 sm:space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-1.5 sm:gap-2 text-xs">
                    <div className="font-extrabold text-slate-900 flex items-center gap-1.5 sm:gap-2">
                      <span className="text-slate-500">Peringkat:</span>
                      <span className="text-asmm-red font-black">{activeSubRank.name}</span>
                    </div>
                    {activeSubRank.culaLabel ? (
                      <span className="text-[10px] sm:text-[11px] font-extrabold bg-white text-slate-700 px-2 py-0.5 rounded-lg border border-slate-200 shadow-2xs">
                        {activeSubRank.culaLabel}
                      </span>
                    ) : null}
                  </div>

                  {/* Bengkung Belt Graphic with Natural Breathing Room */}
                  <div className="py-2.5 sm:py-6 flex items-center justify-center min-h-[70px] sm:min-h-[110px]">
                    <img
                      src={activeSubRank.badgeImg}
                      alt={`${level.module} - ${activeSubRank.name}`}
                      className="w-full max-w-xl h-auto max-h-16 sm:max-h-24 md:max-h-28 object-contain drop-shadow-xs transition-all duration-300"
                    />
                  </div>

                  {/* Cula Rank Selector Buttons (styled identically to category pills - hidden on mobile) */}
                  {level.subRanks.length > 1 && (
                    <div className="hidden sm:flex pt-3 border-t border-slate-200/70 items-center justify-between gap-3">
                      <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider shrink-0">
                        Pilih Cula:
                      </span>
                      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
                        {level.subRanks.map((sub, rIdx) => {
                          const isSelected = currentCulaIdx === rIdx;
                          return (
                            <button
                              key={sub.code}
                              onClick={() =>
                                setSelectedCula((prev) => ({
                                  ...prev,
                                  [level.id]: rIdx
                                }))
                              }
                              className={`px-3 py-1.5 rounded-xl text-xs font-extrabold whitespace-nowrap transition-all cursor-pointer shrink-0 ${isSelected
                                ? 'bg-asmm-red text-white shadow-xs'
                                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100 active:bg-slate-100'
                                }`}
                            >
                              <span>{sub.code}</span>
                              {sub.culaLabel ? (
                                <span className={`ml-1 text-[10px] ${isSelected ? 'text-amber-200' : 'text-slate-400'}`}>
                                  ({sub.culaLabel})
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* 4.3 Newline Content Below Bengkung (Description & Curriculum Flow) */}
                <div className="space-y-3 sm:space-y-4">
                  {/* Full Description */}
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium">
                    {level.description}
                  </p>

                  {/* 2-Column Grid: Sub-ranks & Key Syllabus */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 pt-1 sm:pt-2">
                    {/* Left: Hierarki Peringkat */}
                    <div className="p-3.5 sm:p-4 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-2.5">
                      <div className="flex items-center justify-between border-b border-slate-200/70 pb-2">
                        <div className="flex items-center gap-2 text-xs font-extrabold text-slate-900">
                          <Layers className="w-4 h-4 text-asmm-red" />
                          <span>{level.subRanks.length > 1 ? 'Peringkat & Peranan Cula' : 'Peringkat & Peranan'}</span>
                        </div>
                        {level.subRanks.length > 1 && (
                          <span className="text-[10px] text-slate-400 font-medium hidden sm:inline">Ketik untuk lihat</span>
                        )}
                      </div>

                      <div className="space-y-1.5 sm:space-y-2">
                        {level.subRanks.map((sub, sIdx) => {
                          const isSelected = currentCulaIdx === sIdx;
                          return (
                            <div
                              key={sub.code}
                              onClick={() =>
                                setSelectedCula((prev) => ({
                                  ...prev,
                                  [level.id]: sIdx
                                }))
                              }
                              className={`p-2.5 sm:p-3 rounded-xl border text-xs transition-all cursor-pointer ${isSelected
                                ? 'bg-white border-slate-300 shadow-2xs ring-1 ring-slate-300'
                                : 'bg-white/70 border-slate-200/80 hover:bg-white'
                                }`}
                            >
                              <div className="flex items-center justify-between font-bold text-slate-900">
                                <div className="flex items-center gap-1.5">
                                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isSelected ? 'bg-asmm-red' : 'bg-slate-300'}`} />
                                  <span>{sub.name}</span>
                                </div>
                                {sub.culaLabel ? (
                                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold shrink-0">
                                    {sub.culaLabel}
                                  </span>
                                ) : null}
                              </div>
                              <p className="text-[11px] text-slate-500 mt-1 pl-3 leading-normal">
                                {sub.roleDescription}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Right: Silibus Utama & Syarat Ujian */}
                    <div className="p-3.5 sm:p-4 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-3 flex flex-col justify-between">
                      <div className="space-y-2.5">
                        <div className="flex items-center gap-2 text-xs font-extrabold text-slate-900 border-b border-slate-200/70 pb-2">
                          <Swords className="w-4 h-4 text-asmm-red" />
                          <span>Fokus Sukatan Pelajaran</span>
                        </div>

                        <ul className="space-y-1.5 text-xs text-slate-700 font-medium">
                          {level.highlights.map((h, hIdx) => (
                            <li key={hIdx} className="flex items-start gap-2">
                              <CheckCircle2 className="w-3.5 h-3.5 text-asmm-red shrink-0 mt-0.5" />
                              <span className="leading-tight">{h}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="pt-2.5 border-t border-slate-200/70 space-y-1.5">
                        <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-slate-900">
                          <GraduationCap className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                          <span>Syarat Kelayakan Ujian:</span>
                        </div>
                        <div className="space-y-1 text-[11px] text-slate-500">
                          {level.examRequirements.map((req, rIdx) => (
                            <div key={rIdx} className="flex items-start gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-1.5" />
                              <span className="leading-tight">{req}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PublicSyllabusPage;

